import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { carsApi } from '../api/cars.js';
import { PlateNumber } from '../components/ui/PlateNumber.jsx';
import { Badge } from '../components/ui/Badge.jsx';
import { daysUntil, expiryStatus, formatRelative } from '../utils/dates.js';
import { documentLabel } from '../api/documents.js';

// Agregează: pentru fiecare mașină calculează cel mai urgent document activ.
// Returnează: cars sortate după urgență desc + totals pentru card-urile de stats.
function computeDashboard(cars) {
  const urgencyRank = { expired: 4, danger: 3, warn: 2, ok: 1, null: 0 };
  let urgentCount = 0;
  let activeRentals = 0;

  const enriched = cars.map((c) => {
    activeRentals += c._count?.rentals || 0;

    let worstStatus = null;
    let worstDoc = null;
    for (const d of c.documents || []) {
      const s = expiryStatus(d.data_expirare);
      if (urgencyRank[s] > urgencyRank[worstStatus]) {
        worstStatus = s;
        worstDoc = d;
      }
    }

    // Număr docs ≤5 zile (inclusiv expirate)
    const urgentOnCar = (c.documents || []).filter((d) => {
      const days = daysUntil(d.data_expirare);
      return days !== null && days <= 5;
    });
    urgentCount += urgentOnCar.length;

    return { ...c, worstStatus, worstDoc, urgentDocsCount: urgentOnCar.length };
  });

  const prioritized = enriched
    .filter((c) => c.urgentDocsCount > 0)
    .sort((a, b) => (urgencyRank[b.worstStatus] || 0) - (urgencyRank[a.worstStatus] || 0));

  return { cars: enriched, prioritized, urgentCount, activeRentals };
}

function UrgencyChip({ doc }) {
  const status = expiryStatus(doc.data_expirare);
  const d = daysUntil(doc.data_expirare);
  if (status === 'expired') return <Badge tone="danger">Expirat {formatRelative(doc.data_expirare)}</Badge>;
  if (status === 'danger') return <Badge tone="danger">În {d} zile</Badge>;
  if (status === 'warn') return <Badge tone="warn">În {d} zile</Badge>;
  return <Badge tone="muted">—</Badge>;
}

export function Dashboard() {
  const { user } = useAuth();
  const [cars, setCars] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    carsApi.list()
      .then((d) => setCars(d.cars))
      .catch((ex) => setErr(ex.body?.error || 'Eroare'));
  }, []);

  const dash = useMemo(() => (cars ? computeDashboard(cars) : null), [cars]);

  return (
    <div className="space-y-6">
      <section>
        <h2 className="text-3xl font-semibold tracking-tight text-ink">
          Bună, <span className="text-teal-bright">{user?.name || ''}</span>.
        </h2>
        <p className="mt-1 text-sm text-muted">Flota {user?.tenant?.name}.</p>
      </section>

      {err && <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>}

      {/* Card mare ink cu dot pulse */}
      <section className="relative overflow-hidden rounded-card bg-ink p-6 text-cream">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-coral">
              <span className="h-2 w-2 animate-pulse-dot rounded-full bg-coral" />
              Atenție imediată
            </div>
            <div className="mt-2 text-6xl font-semibold tracking-tight">
              {dash ? dash.urgentCount : '…'}
            </div>
            <p className="text-sm text-cream/80">
              document{dash?.urgentCount === 1 ? '' : 'e'} expiră în ≤5 zile
            </p>
          </div>
          {dash && dash.prioritized.length > 0 && (
            <Link to="/alerts" className="btn bg-coral text-white hover:brightness-95">Vezi toate</Link>
          )}
        </div>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-2 gap-3">
        <Link to="/cars" className="card block hover:shadow-card-hover transition-shadow">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted">Total mașini</div>
          <div className="mt-1 text-4xl font-semibold tracking-tight text-ink">{dash ? dash.cars.length : '…'}</div>
        </Link>
        <div className="card">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted">Închirieri active</div>
          <div className="mt-1 text-4xl font-semibold tracking-tight text-ink">{dash ? dash.activeRentals : '…'}</div>
        </div>
      </section>

      {/* Listă prioritizată */}
      {dash && dash.prioritized.length > 0 && (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">Necesită atenție</h3>
          <div className="space-y-2">
            {dash.prioritized.slice(0, 6).map((car) => (
              <Link
                key={car.id}
                to={`/cars/${car.id}`}
                className="card flex items-center justify-between gap-3 hover:shadow-card-hover"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <PlateNumber value={car.numar_inmatriculare} size="sm" />
                    <span className="truncate text-sm text-ink">{car.marca} {car.model}</span>
                  </div>
                  {car.worstDoc && (
                    <div className="mt-1 text-xs text-muted">
                      <span className="font-semibold text-ink">{documentLabel(car.worstDoc.tip)}</span>
                      {' · '}
                      <UrgencyChip doc={car.worstDoc} />
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {dash && dash.cars.length === 0 && (
        <section className="card text-center">
          <p className="text-sm text-muted">Nu există mașini în flotă încă.</p>
          <Link to="/cars/new" className="mt-3 btn-primary inline-flex">+ Adaugă prima mașină</Link>
        </section>
      )}

      {dash && dash.cars.length > 0 && dash.urgentCount === 0 && (
        <section className="card text-center">
          <p className="text-sm text-muted">Toate documentele sunt în regulă.</p>
        </section>
      )}
    </div>
  );
}
